import { useEffect, useId, useMemo, useRef } from "react";
import { useExternalDataStore } from "../../state/external-data-store";
import { filterClass } from "../../utils/class-utils";
import {
  getAccordionButtonClass,
  getAccordionCollapseBaseClass,
  getAccordionItemClass,
  getAccordionToggleButtonProps,
  isEasyblocksEditorMode,
  resolveAccordionExpanded,
  resolveAccordionStartOpen,
} from "../utils/accordion-shell";
import { CardItemIdRoot } from "./shared/item-id-comment";
import type { DocumentsAccordionProps } from "./types";

function getDocumentBasename(value?: string): string {
  if (!value) {
    return "";
  }

  const withoutQuery = value.split("?")[0];
  return withoutQuery.split("/").pop() || withoutQuery;
}

function getDocumentMetaText(fileType: string, publishDate?: string): string {
  const parts: string[] = [];

  if (fileType && fileType !== "Unknown") {
    parts.push(fileType);
  }
  if (publishDate) {
    parts.push(publishDate);
  }

  return parts.join(" / ");
}

export function DocumentsAccordion(props: DocumentsAccordionProps) {
  const {
    bgClass = "no-class",
    paddingYClass = "py-section-md",
    fixedCategoryId = 0,
    categoryTitleOverride = "",
    fixedYear = 0,
    viewMode = "accordion",
    accordionStartOpen = "false",
  } = props;

  const isStartOpen = resolveAccordionStartOpen(accordionStartOpen);

  const instanceIdRef = useRef<string>(`documents-accordion-${Math.random().toString(36).slice(2, 11)}`);
  const instanceId = instanceIdRef.current;

  const documentsState = useExternalDataStore((state) => state.documentsBrowser[instanceId]);
  const listDocuments = useExternalDataStore((state) => state.listDocuments);
  const listDocumentCategories = useExternalDataStore((state) => state.listDocumentCategories);
  const setDocumentsBrowserCategoryId = useExternalDataStore((state) => state.setDocumentsBrowserCategoryId);
  const setDocumentsBrowserYear = useExternalDataStore((state) => state.setDocumentsBrowserYear);
  const setDocumentsBrowserSortBy = useExternalDataStore((state) => state.setDocumentsBrowserSortBy);
  const setDocumentsBrowserSortDirection = useExternalDataStore((state) => state.setDocumentsBrowserSortDirection);
  const resetDocumentsBrowser = useExternalDataStore((state) => state.resetDocumentsBrowser);

  const categoryId = documentsState?.categoryId ?? 0;
  const year = documentsState?.year ?? 0;
  const sortBy = documentsState?.sortBy ?? "postedDate";
  const sortDirection = documentsState?.sortDirection ?? "desc";
  const categories = documentsState?.categories ?? [];
  const allItems = documentsState?.allItems ?? [];
  const items = documentsState?.items ?? [];
  const isLoading = documentsState?.isLoading ?? true;
  const listError = documentsState?.listError ?? null;
  const isLoadingCategories = documentsState?.isLoadingCategories ?? false;
  const categoriesError = documentsState?.categoriesError ?? null;

  const selectOptions = useMemo(() => [{ id: 0, name: "All categories" }, ...categories], [categories]);
  const parsedFixedCategoryId = Number(fixedCategoryId);
  const normalizedFixedCategoryId =
    Number.isFinite(parsedFixedCategoryId) && parsedFixedCategoryId > 0 ? parsedFixedCategoryId : 0;
  const isFixedCategory = normalizedFixedCategoryId > 0;
  const effectiveCategoryId = isFixedCategory ? normalizedFixedCategoryId : categoryId;
  const parsedFixedYear = Number(fixedYear);
  const normalizedFixedYear = Number.isFinite(parsedFixedYear) && parsedFixedYear > 0 ? parsedFixedYear : 0;
  const isFixedYear = normalizedFixedYear > 0;
  const effectiveYear = isFixedYear ? normalizedFixedYear : year;
  const yearOptions = useMemo(() => {
    const years = new Set<number>();
    allItems.forEach((item) => {
      const parsed = Number(item.year);
      if (Number.isFinite(parsed) && parsed > 0) {
        years.add(parsed);
      }
    });
    return [0, ...Array.from(years).sort((a, b) => b - a)];
  }, [allItems]);
  const normalizedCategoryTitleOverride =
    typeof categoryTitleOverride === "string" ? categoryTitleOverride.trim() : "";
  const activeCategoryTitle = useMemo(() => {
    if (normalizedCategoryTitleOverride) return normalizedCategoryTitleOverride;
    if (effectiveCategoryId <= 0) return "Documents";
    const category = categories.find((item) => item.id === effectiveCategoryId);
    return category?.name ?? "Documents";
  }, [categories, effectiveCategoryId, normalizedCategoryTitleOverride]);
  const activeSectionTitle = useMemo(() => {
    if (effectiveYear <= 0) return activeCategoryTitle;
    return `${activeCategoryTitle} (${effectiveYear})`;
  }, [activeCategoryTitle, effectiveYear]);

  const reactId = useId();
  const accordionId = `documents-accordion-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const isInEditor = isEasyblocksEditorMode();

  const sectionClasses = [bgClass, filterClass(paddingYClass)]
    .map(filterClass)
    .filter(Boolean)
    .join(" ");

  const isAccordionExpanded = resolveAccordionExpanded(isInEditor, isStartOpen);

  /**
   * ===== CLASSES (frontend can edit here) =====
   */
  const classes = {
    section: ["section section-documents", sectionClasses].filter(Boolean).join(" ").trim(),
    container: "container-xl",
    sectionTitle: "mb-3",
    tableWrap: "table-responsive",
    table: "table table-striped table-hover align-middle mb-0",
    accordion: "accordion",
    accordionItem: getAccordionItemClass(),
    accordionHeader: "accordion-header",
    accordionButton: (expanded: boolean) => getAccordionButtonClass(isInEditor, expanded),
    accordionCollapse: getAccordionCollapseBaseClass(isInEditor),
    accordionBody: "accordion-body d-flex flex-column gap-3 overflow-hidden",
    listWrap: "d-flex flex-column gap-3 overflow-hidden",
    controls: "row g-3 w-100",
    controlFieldCategory: "col-12 col-md-4",
    controlFieldYear: "col-12 col-md-3",
    controlFieldSort: "col-12 col-md-5",
    selectLabel: "form-label small mb-1",
    select: "form-select form-select-sm w-100",
    sortSelectRow: "d-flex flex-column flex-sm-row gap-2 w-100",
    sortSelect: "form-select form-select-sm w-100 flex-grow-1",
    directionSelect: "form-select form-select-sm w-100 flex-grow-1",
    loading: "d-flex align-items-center gap-2 small",
    error: "alert alert-warning py-2 px-3 small mb-0",
    list: "list-group overflow-hidden",
    listItem: "list-group-item d-flex flex-column gap-1 min-w-0",
    documentMain: "d-flex flex-column gap-1 min-w-0 w-100",
    documentTitle: "fw-semibold min-w-0",
    titleLink: "text-decoration-none text-break",
    titleDisabled: "text-break",
    documentMetaRow: "d-flex flex-row justify-content-between align-items-start gap-2 min-w-0 w-100",
    documentFilename: "text-gray-600 fs-14 fst-italic text-break min-w-0 me-2",
    documentMeta: "text-gray-600 fs-14 fst-italic text-nowrap flex-shrink-0 text-end ms-auto",
    empty: "small",
    editorNote: "alert alert-info mb-0",
  };

  useEffect(() => {
    void listDocumentCategories(instanceId);
    return () => resetDocumentsBrowser(instanceId);
  }, [instanceId, listDocumentCategories, resetDocumentsBrowser]);

  useEffect(() => {
    void listDocuments(instanceId, effectiveCategoryId, effectiveYear);
  }, [effectiveCategoryId, effectiveYear, instanceId, listDocuments]);

  const renderEditorNote = () => (
    <div className={classes.editorNote} role="status">
      Documents are hidden in edit mode. They will appear in the live view.
    </div>
  );

  const renderNoDataNote = () => (
    <div className={classes.editorNote} role="status">
      No documents available at this time. This section will not appear in the live view until document data is available.
    </div>
  );

  const renderEditorContent = () =>
    !isLoading && !listError && allItems.length === 0 ? renderNoDataNote() : renderEditorNote();

  const sortDirectionOptions =
    sortBy === "name"
      ? [
          { value: "asc", label: "A to Z" },
          { value: "desc", label: "Z to A" },
        ]
      : [
          { value: "desc", label: "Newest first" },
          { value: "asc", label: "Oldest first" },
        ];

  const renderDocumentsList = () => (
    <>
        <div className={classes.controls}>
          {!isFixedCategory ? (
            <div className={classes.controlFieldCategory}>
              <label htmlFor={`${accordionId}-category`} className={classes.selectLabel}>
                Category
              </label>
              <select
                id={`${accordionId}-category`}
                className={classes.select}
                value={categoryId}
                onChange={(e) => setDocumentsBrowserCategoryId(instanceId, Number(e.target.value))}
                disabled={isLoadingCategories}
              >
                {selectOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {!isFixedYear ? (
            <div className={classes.controlFieldYear}>
              <label htmlFor={`${accordionId}-year`} className={classes.selectLabel}>
                Year
              </label>
              <select
                id={`${accordionId}-year`}
                className={classes.select}
                value={year}
                onChange={(e) => setDocumentsBrowserYear(instanceId, Number(e.target.value))}
                disabled={isLoading}
              >
                {yearOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt === 0 ? "All years" : opt}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className={classes.controlFieldSort}>
            <label htmlFor={`${accordionId}-sort`} className={classes.selectLabel}>
              Sort By
            </label>
            <div className={classes.sortSelectRow}>
              <select
                id={`${accordionId}-sort`}
                className={classes.sortSelect}
                value={sortBy}
                onChange={(e) =>
                  setDocumentsBrowserSortBy(instanceId, e.target.value === "name" ? "name" : "postedDate")
                }
                disabled={isLoading}
              >
                <option value="postedDate">Posted Date</option>
                <option value="name">Name</option>
              </select>
              <select
                id={`${accordionId}-sort-direction`}
                className={classes.directionSelect}
                value={sortDirection}
                onChange={(e) =>
                  setDocumentsBrowserSortDirection(instanceId, e.target.value === "asc" ? "asc" : "desc")
                }
                disabled={isLoading}
                aria-label="Sort direction"
              >
                {sortDirectionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

      {!isFixedCategory && isLoadingCategories ? (
        <div className={classes.loading}>
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          Loading categories...
        </div>
      ) : null}

      {!isFixedCategory && !isLoadingCategories && categoriesError ? (
        <div className={classes.error} role="alert">
          We could not load categories. Please try again in a moment.
        </div>
      ) : null}

      {isLoading ? (
        <div className={classes.loading}>
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          Loading documents...
        </div>
      ) : null}

      {!isLoading && listError ? (
        <div className={classes.error} role="alert">
          We could not load documents. Please try again in a moment.
        </div>
      ) : null}

      {!isLoading && !listError ? (
        <div className={classes.list}>
          {items.length === 0 ? (
            <div className={classes.empty}>
              {isInEditor
                ? "No documents available for the selected category. This section will not appear in the live view until documents are available."
                : "No documents available for this category."}
            </div>
          ) : (
            items.map((doc) => {
              const href = doc.url || "";
              const itemName = (doc.item_name || doc.name || "").trim();
              const filename = getDocumentBasename(doc.filename || doc.url);
              const title = itemName || filename || "Document";
              const source = doc.filename || doc.url || "";
              const ext = source.split("?")[0].split(".").pop() || "";
              const fileType = ext ? ext.toUpperCase() : "Unknown";
              const showFilename =
                Boolean(itemName) &&
                Boolean(filename) &&
                filename.toLowerCase() !== itemName.toLowerCase();
              const metaText = getDocumentMetaText(fileType, doc.publishDate);

              return (
                <CardItemIdRoot key={doc.id} itemId={doc.item_id ?? doc.id} className={classes.listItem}>
                  <div className={classes.documentMain}>
                    <div className={classes.documentTitle}>
                      {href ? (
                        <a
                          href={href}
                          className={classes.titleLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-describedby="new_window_description"
                        >
                          {title}
                        </a>
                      ) : (
                        <span className={classes.titleDisabled}>{title}</span>
                      )}
                    </div>

                    {showFilename || metaText ? (
                      <div className={classes.documentMetaRow}>
                        {showFilename ? (
                          <div className={classes.documentFilename}>{filename}</div>
                        ) : null}
                        {metaText ? <div className={classes.documentMeta}>{metaText}</div> : null}
                      </div>
                    ) : null}
                  </div>
                </CardItemIdRoot>
              );
            })
          )}
        </div>
      ) : null}
    </>
  );

  // In live view, hide the entire section if there's no data
  if (!isInEditor && !isLoading && !listError && allItems.length === 0) {
    return null;
  }

  return (
    <section className={classes.section}>
      <div className={classes.container}>
        {viewMode === "accordion" ? (
          <div className={classes.accordion} id={accordionId}>
            <div className={classes.accordionItem}>
              <h2 className={classes.accordionHeader} id={`${accordionId}-heading`}>
                <button
                  className={classes.accordionButton(isAccordionExpanded)}
                  type="button"
                  aria-expanded={isAccordionExpanded ? "true" : "false"}
                  aria-controls={`${accordionId}-collapse`}
                  {...getAccordionToggleButtonProps(isInEditor, `${accordionId}-collapse`)}
                >
                  <span className="h4 mb-0">{activeSectionTitle}</span>
                </button>
              </h2>
              <div
                id={`${accordionId}-collapse`}
                className={[
                  classes.accordionCollapse,
                  isInEditor ? "show" : isStartOpen ? "show" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-labelledby={`${accordionId}-heading`}
              >
                <div className={classes.accordionBody}>
                  {isInEditor ? renderEditorContent() : renderDocumentsList()}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <h2 className={classes.sectionTitle}>{activeSectionTitle}</h2>
            {isInEditor ? renderEditorNote() : (
              <div className={classes.listWrap}>
                {renderDocumentsList()}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default DocumentsAccordion;
